import { useEffect, useState } from "react";
import { GlassCard } from "../components/ui/glass-card";
import { GradientText } from "../components/ui/gradient-text";
import { Button } from "../components/ui/button";
import { LimitedInput } from "../components/ui/limited-input";
import {
  clampFieldValue,
  FIELD_LIMITS,
  clampToLimit,
  sanitizeCardFromApi,
} from "../utils/fieldLimits";
import { Label } from "../components/ui/label";
import { FloatingActionButton } from "../components/layout/floating-action-button";
import type { BusinessCard } from "../models/business-card.model";
import {
  CreditCard,
  User,
  Building2,
  Mail,
  Palette,
  Upload,
  Eye,
  Edit3,
  Plus,
  Trash2,
  ListOrdered,
  Trash,
  Hash,
  FileText,
  Globe,
  AlertTriangle,
  ArrowRight,
  Moon,
  Sun,
} from "lucide-react";
import {
  generateId,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonToolbar,
  IonFab,
  IonFabButton,
  IonList,
  IonReorderGroup,
  IonItem,
  IonLabel,
  IonReorder,
} from "@ionic/react";
import {
  BusinessCardPreview,
  socialPlatforms,
} from "../components/cards/business-card-preview";

import {
  eyeOutline,
  createOutline,
  settingsOutline,
  idCardOutline,
  saveOutline,
} from "ionicons/icons";
import { useHistory } from "react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useModal } from "../contexts/ModalContext";
import api from "../services/api";
import { cn } from "../components/lib/utils";
import { Spinner } from "@radix-ui/themes";
import { SafeArea } from "capacitor-plugin-safe-area";
import {
  getAnalogousColor,
  getRelativeLuminance,
  hexToRgb,
} from "../utils/colorTheme";
import ColorPiker from "../components/ui/color-piker";
import { useKeyboardHeight } from "../hooks/useKeyboardHeight";

export default function CardsPage() {
  const [showPreview, setShowPreview] = useState<boolean | null>(null);
  const history = useHistory();
  const { showAlert, showConfirm } = useModal();
  const API_BASE_URL = (api as any)?.defaults?.baseURL || "";
  const keyboardHeight = useKeyboardHeight();

  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingPergonalPicture, setIsLoadingPersonalPicture] =
    useState(false);
  const [isLoadingCompanyPicture, setIsLoadingCompanyPicture] = useState(false);
  const [profilePicSuccess, setProfilePicSuccess] = useState(false);
  const [companyPicSuccess, setCompanyPicSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [accountStatus, setAccountStatus] = useState<{
    is_temp: boolean;
    temp_expires_at?: string | null;
  } | null>(null);

  const tempDaysLeft = (() => {
    if (!accountStatus?.is_temp || !accountStatus.temp_expires_at) return null;
    const exp = new Date(accountStatus.temp_expires_at);
    const diff = exp.getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  useEffect(() => {
    const loadAccountStatus = async () => {
      try {
        const res = await api.get("/me");
        setAccountStatus({
          is_temp: !!res.data.is_temp,
          temp_expires_at: res.data.temp_expires_at,
        });
      } catch {
        setAccountStatus(null);
      }
    };
    loadAccountStatus();
  }, []);

  const useWindowSize = () => {
    const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
    useEffect(() => {
      const handleResize = () =>
        setSize([window.innerWidth, window.innerHeight]);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return size;
  };
  const [width] = useWindowSize();
  const isMobileView = width < 992;

  const [card, setCard] = useState<BusinessCard>({
    id: "",
    first_name: "",
    last_name: "",
    job_title: "",
    company_name: "",
    email: "",
    phone: "",
    website: "",
    profile_pic: "",
    company_logo: "",
    company_motto: "",
    vat_number: "",
    sdi_code: "",
    bg_type: "gradient",
    bg_value: "#F97316", // gradient color 2
    primary_color: "#EA580C", // gradient color 1
    text_color: "#ffffff",
    theme_mode: "dark", // 'dark' or 'light'
    address: {
      street: "",
      street_number: "",
      city: "",
      state: "",
    },
    secondary_emails: [],
    secondary_phones: [],
    socials: [],
    section_order: ["company", "address", "secondary", "social"],
  });

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await api.get("/cards/me");
        if (res.data) {
          const cardData = sanitizeCardFromApi(res.data) as typeof res.data;
          // State migration for colors
          if (cardData.theme_mode && cardData.theme_mode.startsWith("#")) {
            const rgb = hexToRgb(cardData.theme_mode);
            if (rgb) {
              const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
              cardData.theme_mode = luminance < 0.5 ? "dark" : "light";
            } else {
              cardData.theme_mode = "dark";
            }
          } else if (!cardData.theme_mode) {
            cardData.theme_mode = "dark";
          }

          // Set default gradient colors if not present
          if (!cardData.primary_color) {
            cardData.primary_color = "#EA580C"; // default gradient color 1
          }
          if (!cardData.bg_value) {
            cardData.bg_value = "#F97316"; // default gradient color 2
          }

          setCard((prev: any) => ({
            ...prev,
            ...cardData,
            // Garantisce preset dei nuovi campi opzionali se mancanti in DB
            company_name: cardData.company_name || "",
            company_motto: cardData.company_motto || "",
            company_logo: cardData.company_logo || "",
            vat_number: cardData.vat_number || "",
            sdi_code: cardData.sdi_code || "",
            job_title: res.data.job_title || "",
            secondary_phones: res.data.secondary_phones || [],
            secondary_emails: res.data.secondary_emails || [],
            address: res.data.address || {
              street: "",
              street_number: "",
              city: "",
              state: "",
            },
            socials: res.data.socials || [],
            section_order: res.data.section_order || [
              "company",
              "address",
              "secondary",
              "social",
            ],
          }));
          //setIsEditing(true);
        }
      } catch (error: any) {
        if (
          error?.response?.status === 401 ||
          error?.response?.status === 403
        ) {
          return;
        }
        try {
          const me = await api.get("/me");
          if (me.data) {
            setCard((prev: any) => ({
              ...prev,
              first_name: me.data.first_name || prev.first_name,
              last_name: me.data.last_name || prev.last_name,
              email: me.data.email || prev.email,
              company_name: me.data.company_name || prev.company_name,
            }));
          }
        } catch {}
      }
    };
    fetchCard();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setHasUnsavedChanges(true);
    value = clampFieldValue(field, value);
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setCard((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof BusinessCard] as any),
          [child]: value,
        },
      }));
    } else {
      setCard((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleArrayChange = (
    arrayName: "secondary_emails" | "secondary_phones" | "socials",
    index: number,
    field: string,
    value: string,
  ) => {
    setHasUnsavedChanges(true);
    if (arrayName === "secondary_emails") {
      if (field === "label")
        value = clampToLimit(value, FIELD_LIMITS.secondary_email_label);
      if (field === "email")
        value = clampToLimit(value, FIELD_LIMITS.secondary_email);
    } else if (arrayName === "secondary_phones") {
      if (field === "label")
        value = clampToLimit(value, FIELD_LIMITS.secondary_phone_label);
      if (field === "number")
        value = clampToLimit(value, FIELD_LIMITS.secondary_phone_number);
    } else if (arrayName === "socials" && field === "username") {
      value = clampToLimit(value, FIELD_LIMITS.social_username);
    }
    setCard((prev) => ({
      ...prev,
      [arrayName]:
        prev[arrayName]?.map((item, i) =>
          i === index ? { ...item, [field]: value } : item,
        ) || [],
    }));
  };

  const handleImageUpload = (field: "profile_pic" | "company_logo") => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (!file.type.startsWith("image/")) {
          alert("Seleziona solo immagini");
          input.value = "";
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          // 5MB limit
          alert("Il file è troppo grande. Massimo 5MB.");
          return;
        }

        try {
          switch (field) {
            case "company_logo":
              setIsLoadingCompanyPicture(true);
              break;
            case "profile_pic":
              setIsLoadingPersonalPicture(true);
              break;
          }

          const token = localStorage.getItem("token");
          const formData = new FormData();
          formData.append("file", file);
          const res = await api.post("/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          });
          const url = res.data?.url ? `${API_BASE_URL}${res.data.url}` : "";
          if (url) {
            setHasUnsavedChanges(true);
            handleInputChange(field, url);
            // Show success feedback
            if (field === "company_logo") {
              setCompanyPicSuccess(true);
              setTimeout(() => setCompanyPicSuccess(false), 3000);
            } else if (field === "profile_pic") {
              setProfilePicSuccess(true);
              setTimeout(() => setProfilePicSuccess(false), 3000);
            }
          }
        } catch (e) {
          console.error("Upload failed", e);
          showAlert("Errore", "Caricamento non riuscito. Riprova.");
        } finally {
          //setUploading((p) => ({ ...p, [field]: false }));
          switch (field) {
            case "company_logo":
              setIsLoadingCompanyPicture(false);
              break;
            case "profile_pic":
              setIsLoadingPersonalPicture(false);
              break;
          }
        }
      }
    };
    input.click();
  };

  const socialUrlPatterns: Record<string, RegExp> = {
    Instagram: /instagram\.com/i,
    LinkedIn: /linkedin\.com/i,
    Facebook: /facebook\.com/i,
    "X (Twitter)": /(twitter\.com|x\.com)/i,
    TikTok: /tiktok\.com/i,
    GitHub: /github\.com/i,
    YouTube: /youtube\.com|youtu\.be/i,
    Telegram: /(t\.me|telegram\.me|telegram\.org)/i,
    WhatsApp: /(wa\.me|whatsapp\.com)/i,
  };

  const isValidSocialUrl = (platform: string, url: string): boolean => {
    if (!url || !platform) return true;
    const pattern = socialUrlPatterns[platform];
    if (!pattern) return true;
    return pattern.test(url);
  };

  const colorPresets = [
    {
      name: "Rosa Elegante",
      background: "#E91E63",
      card: "#F8F8F8",
      icon: "#E91E63",
    },
    {
      name: "Blu Professionale",
      background: "#1E40AF",
      card: "#F0F4F8",
      icon: "#3B82F6",
    },
    {
      name: "Verde Natura",
      background: "#10B981",
      card: "#FFFFFF",
      icon: "#059669",
    },
    {
      name: "Viola Creativo",
      background: "#8B5CF6",
      card: "#FAF5FF",
      icon: "#7C3AED",
    },
    {
      name: "Arancio Energia",
      background: "#F97316",
      card: "#FFF7ED",
      icon: "#EA580C",
    },
    {
      name: "Grigio Minimalista",
      background: "#374151",
      card: "#F9FAFB",
      icon: "#6B7280",
    },
  ];

  const applyColorPreset = (preset: (typeof colorPresets)[0]) => {
    setHasUnsavedChanges(true);
    setCard(
      (prev) =>
        ({
          ...prev,
          bg_type: "gradient",
          bg_value: preset.background, // Sfondo
          theme_mode: preset.card, // Card
          primary_color: preset.icon, // Icone
        } as any),
    );
  };

  const handleAutoColor = () => {
    if (card.primary_color) {
      const analogousColor = getAnalogousColor(card.primary_color);
      handleInputChange("bg_value", analogousColor);
    }
  };

  const addSecondaryEmail = () => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      secondary_emails: [
        ...(prev.secondary_emails || []),
        { label: "", email: "" },
      ],
    }));
  };

  const removeSecondaryEmail = (index: number) => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      secondary_emails:
        prev.secondary_emails?.filter((_, i) => i !== index) || [],
    }));
  };

  const addSecondaryPhone = () => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      secondary_phones: [
        ...(prev.secondary_phones || []),
        { label: "", number: "" },
      ],
    }));
  };

  const removeSecondaryPhone = (index: number) => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      secondary_phones:
        prev.secondary_phones?.filter((_, i) => i !== index) || [],
    }));
  };

  const addSocial = () => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      socials: [...(prev.socials || []), { platform: "", username: "" }],
    }));
  };

  const removeSocial = (index: number) => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      socials: prev.socials?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSectionOrderChange = (newOrder: string[]) => {
    setHasUnsavedChanges(true);
    setCard((prev) => ({
      ...prev,
      section_order: newOrder,
    }));
  };

  const deleteImg = (imgProp: string) => {
    showConfirm(
      "Elimina Immagine",
      "Sei sicuro di voler eliminare l'immagine?",
      async () => {
        handleInputChange(imgProp, "");
      },
    );
  };

  const submit = async () => {
    const token = localStorage.getItem("token");
    try {
      let payload = { ...card };

      // Block save if any social link doesn't match its platform
      const invalidSocials = (payload.socials ?? []).filter(
        (s) =>
          s.platform &&
          s.username &&
          !isValidSocialUrl(s.platform, s.username),
      );
      if (invalidSocials.length > 0) {
        const names = invalidSocials.map((s) => s.platform).join(", ");
        showAlert(
          "Link social non valido",
          `Per sicurezza, il link inserito per ${names} deve corrispondere alla piattaforma selezionata. Inserisci l'URL corretto oppure rimuovi il social.`,
        );
        return;
      }
      setIsLoadingSave(true);
      const res = await api.put(
        "/cards",
        { ...payload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.id) {
        setCard({ ...payload, id: res.data.id });
        setHasUnsavedChanges(false);
        history.push(`/card/${res.data.id}`);
        //setIsEditing(true);
        // setToast({
        //   show: true,
        //   message: isEditing
        //     ? "Aggiornato con successo"
        //     : "Creato con successo",
        //   color: "success",
        // });
        // User stays on the create/edit page after saving
      }
    } catch (error) {
      console.error("Failed to save card", error);
      // setToast({
      //   show: true,
      //   message: "Errore nel salvataggio",
      //   color: "danger",
      // });
    } finally {
      setIsLoadingSave(false);
    }
  };

  const [safeArea, setSafeArea] = useState<any>({ top: 0 });
  useEffect(() => {
    SafeArea.getSafeAreaInsets().then((data) => {
      setSafeArea(data.insets);
    });
  }, []);

  return (
    <IonPage>
      <IonHeader
        translucent
        style={{
          paddingLeft: "var(--ion-safe-area-left)",
          // paddingTop: `${safeArea.top}px`,
          paddingRight: "var(--ion-safe-area-right)",
        }}
      >
        <IonToolbar
          color="transparent"
          style={{
            "--background": "transparent",
            borderBottom: "2px solid rgba(255,255,255,0.15)",
          }}
        >
          <img
            className="h-8 ms-6 my-3"
            src="/assets/Ecard_logo_oriz.webp"
            alt="Ecard"
          />

          <IonButtons slot="end">
            <IonButton
              fill="clear"
              color="light"
              onClick={() => history.push("/settings")}
            >
              <IonIcon className="size-6" icon={settingsOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        className="overflow-y-hidden"
        style={{ "--background": "#111113" } as any}
      >
        <div
          className="relative flex flex-col h-full"
          style={{
            background: "transparent",
          }}
        >
          <div className="overflow-y-hidden h-full">
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 overflow-y-auto overflow-x-hidden h-full lg:py-4 lg:px-4">
              {/* Configuration Form */}
              <div
                className={cn(
                  "h-full overflow-y-scroll overflow-x-hidden transition-all duration-300 ease-in-out",
                  isMobileView
                    ? [
                        "absolute inset-0", // mobile: pannelli sovrapposti
                        showPreview
                          ? "-translate-x-full opacity-0"
                          : "translate-x-0 opacity-100",
                      ]
                    : "relative", // desktop: sempre visibile e affiancato
                )}
              >
                <div
                  className="space-y-6 pt-4 lg:pt-0 px-4 lg:px-1"
                  style={{
                    paddingBottom:
                      keyboardHeight > 0 ? keyboardHeight + 16 : undefined,
                    backgroundColor:
                      keyboardHeight > 0 ? "#111113" : undefined,
                  }}
                >
                  {accountStatus?.is_temp && tempDaysLeft !== null && (
                    <div
                      className="mb-4 flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
                      role="status"
                    >
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-400" />
                      <div>
                        <p className="font-medium">Account provvisorio</p>
                        <p className="mt-1 text-amber-100/90">
                          {tempDaysLeft === 0
                            ? "Scade oggi."
                            : tempDaysLeft === 1
                              ? "Hai 1 giorno rimasto"
                              : `Hai ${tempDaysLeft} giorni rimasti`}{" "}
                          per impostare la password personale. Controlla la tua
                          email.
                        </p>
                      </div>
                    </div>
                  )}
                  <GlassCard hover={false} className="">
                    <div className="space-y-6">
                      {/* Personal Information */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Informazioni Personali
                          </Label>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">Nome</Label>
                            <LimitedInput
                              id="firstName"
                              value={card.first_name || ""}
                              onChange={(e) =>
                                handleInputChange("first_name", e.target.value)
                              }
                              placeholder="Mario"
                              maxLength={16}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Cognome</Label>
                            <LimitedInput
                              id="lastName"
                              value={card.last_name || ""}
                              onChange={(e) =>
                                handleInputChange("last_name", e.target.value)
                              }
                              placeholder="Rossi"
                              maxLength={16}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="jobTitle">Ruolo</Label>
                          <LimitedInput
                            id="jobTitle"
                            value={card.job_title || ""}
                            onChange={(e) =>
                              handleInputChange("job_title", e.target.value)
                            }
                            placeholder="CEO & Founder"
                            maxLength={50}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <LimitedInput
                            id="email"
                            type="email"
                            value={card.email || ""}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            placeholder="mario@effia.com"
                            maxLength={100}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone">Telefono</Label>
                          <LimitedInput
                            id="phone"
                            value={card.phone || ""}
                            onChange={(e) =>
                              handleInputChange("phone", e.target.value)
                            }
                            placeholder="+39 123 456 7890"
                            maxLength={20}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Foto Profilo</Label>

                          <div className="flex gap-2 w-full">
                            <Button
                              variant="outline"
                              onClick={() => handleImageUpload("profile_pic")}
                              className={cn(
                                "backdrop-blur-xl border transition-colors grow",
                                profilePicSuccess
                                  ? "bg-green-500/20 border-green-500/50"
                                  : "bg-white/5 border-white/10",
                              )}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {profilePicSuccess
                                ? "✓ Caricato con successo"
                                : "Carica Immagine"}
                              {isLoadingCompanyPicture && <Spinner></Spinner>}
                            </Button>
                            {card.profile_pic && (
                              <Button
                                variant="outline"
                                className="backdrop-blur-xl border transition-colors bg-white/5 border-white/10"
                                onClick={() => deleteImg("profile_pic")}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Company Information */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Informazioni Azienda
                          </Label>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="company">Nome Azienda</Label>
                          <LimitedInput
                            id="company"
                            value={card.company_name || ""}
                            onChange={(e) =>
                              handleInputChange("company_name", e.target.value)
                            }
                            placeholder="EffIA Solutions"
                            maxLength={32}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="motto">Motto Aziendale</Label>
                          <LimitedInput
                            id="motto"
                            value={card.company_motto || ""}
                            onChange={(e) =>
                              handleInputChange("company_motto", e.target.value)
                            }
                            placeholder="Innovazione per il futuro"
                            maxLength={60}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center space-x-1.5">
                            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                            <Label htmlFor="vat">P.IVA</Label>
                          </div>
                          <LimitedInput
                            id="vat"
                            value={card.vat_number || ""}
                            onChange={(e) =>
                              handleInputChange("vat_number", e.target.value)
                            }
                            placeholder="04704470980"
                            maxLength={11}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center space-x-1.5">
                            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                            <Label htmlFor="sdi">Codice SDI</Label>
                          </div>
                          <LimitedInput
                            id="sdi"
                            value={card.sdi_code || ""}
                            onChange={(e) =>
                              handleInputChange("sdi_code", e.target.value.toUpperCase())
                            }
                            placeholder="0000000"
                            maxLength={7}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center space-x-1.5">
                            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                            <Label htmlFor="website">Sito Web</Label>
                          </div>
                          <LimitedInput
                            id="website"
                            value={card.website || ""}
                            onChange={(e) =>
                              handleInputChange("website", e.target.value)
                            }
                            placeholder="https://effia.com"
                            maxLength={100}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Logo Azienda</Label>
                          <div className="flex gap-2 w-full">
                            <Button
                              variant="outline"
                              onClick={() => handleImageUpload("company_logo")}
                              className={cn(
                                "backdrop-blur-xl border transition-colors grow",
                                companyPicSuccess
                                  ? "bg-green-500/20 border-green-500/50"
                                  : "bg-white/5 border-white/10",
                              )}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {companyPicSuccess
                                ? "✓ Caricato con successo"
                                : "Carica Logo"}
                              {isLoadingCompanyPicture && <Spinner></Spinner>}
                            </Button>
                            {card.company_logo && (
                              <Button
                                variant="outline"
                                className="backdrop-blur-xl border transition-colors bg-white/5 border-white/10"
                                onClick={() => deleteImg("company_logo")}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Secondary Emails */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Altri contatti
                          </Label>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Email Aggiuntive
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addSecondaryEmail}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Aggiungi
                          </Button>
                        </div>

                        {card.secondary_emails?.map((email, index) => (
                          <div key={index} className="grid grid-cols-2 gap-2">
                            <LimitedInput
                              placeholder="Etichetta (es. Supporto)"
                              value={email.label || ""}
                              onChange={(e) =>
                                handleArrayChange(
                                  "secondary_emails",
                                  index,
                                  "label",
                                  e.target.value,
                                )
                              }
                              maxLength={20}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                            <div className="flex space-x-2">
                              <LimitedInput
                                placeholder="email@esempio.com"
                                value={email.email || ""}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "secondary_emails",
                                    index,
                                    "email",
                                    e.target.value,
                                  )
                                }
                                maxLength={100}
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeSecondaryEmail(index)}
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Secondary Phones */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Telefoni Aggiuntivi
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addSecondaryPhone}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Aggiungi
                          </Button>
                        </div>

                        {card.secondary_phones?.map((phone, index) => (
                          <div key={index} className="grid grid-cols-2 gap-2">
                            <LimitedInput
                              placeholder="Etichetta (es. Ufficio)"
                              value={phone.label || ""}
                              onChange={(e) =>
                                handleArrayChange(
                                  "secondary_phones",
                                  index,
                                  "label",
                                  e.target.value,
                                )
                              }
                              maxLength={20}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                            <div className="flex space-x-2">
                              <LimitedInput
                                placeholder="+39 123 456 7890"
                                value={phone.number || ""}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "secondary_phones",
                                    index,
                                    "number",
                                    e.target.value,
                                  )
                                }
                                maxLength={20}
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeSecondaryPhone(index)}
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Social Media */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Social Media
                          </Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addSocial}
                            className="backdrop-blur-xl bg-white/5 border border-white/10"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Aggiungi
                          </Button>
                        </div>

                        {card.socials?.map((social, index) => (
                          <div key={index} className="grid grid-cols-2 gap-2">
                            <Select
                              value={social.platform || ""}
                              onValueChange={(value) =>
                                handleArrayChange(
                                  "socials",
                                  index,
                                  "platform",
                                  value,
                                )
                              }
                            >
                              <SelectTrigger className="backdrop-blur-xl bg-white/5 border border-white/10 w-full">
                                <SelectValue placeholder="Piattaforma (es. LinkedIn)" />
                              </SelectTrigger>
                              <SelectContent>
                                {socialPlatforms.map((platform) => (
                                  <SelectItem
                                    key={platform.name}
                                    value={platform.name}
                                  >
                                    <div className="flex gap-4">
                                      {platform.icon}
                                      <span>{platform.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="flex space-x-2">
                              <LimitedInput
                                placeholder="Link (es. https://instagram.com/utente)"
                                value={social.username || ""}
                                onChange={(e) =>
                                  handleArrayChange(
                                    "socials",
                                    index,
                                    "username",
                                    e.target.value,
                                  )
                                }
                                maxLength={200}
                                aria-invalid={
                                  social.username &&
                                  social.platform &&
                                  !isValidSocialUrl(
                                    social.platform,
                                    social.username,
                                  )
                                    ? true
                                    : undefined
                                }
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeSocial(index)}
                                className="backdrop-blur-xl bg-white/5 border border-white/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            {social.username &&
                              social.platform &&
                              !isValidSocialUrl(
                                social.platform,
                                social.username,
                              ) && (
                                <div className="flex items-center gap-1.5 text-xs text-red-400 mt-1">
                                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                                  <span>
                                    Link non valido per {social.platform}: per
                                    sicurezza non è consentito inserire URL di
                                    altre piattaforme. Il salvataggio sarà
                                    bloccato.
                                  </span>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>

                      {/* Address */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Indirizzo
                          </Label>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="street">Via</Label>
                            <LimitedInput
                              id="street"
                              value={card.address?.street || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  "address.street",
                                  e.target.value,
                                )
                              }
                              placeholder="Via Roma"
                              maxLength={32}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="streetNumber">Numero</Label>
                            <LimitedInput
                              id="streetNumber"
                              value={card.address?.street_number || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  "address.street_number",
                                  e.target.value,
                                )
                              }
                              placeholder="123"
                              maxLength={10}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="city">Città</Label>
                            <LimitedInput
                              id="city"
                              value={card.address?.city || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  "address.city",
                                  e.target.value,
                                )
                              }
                              placeholder="Milano"
                              maxLength={32}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">Provincia</Label>
                            <LimitedInput
                              id="state"
                              value={card.address?.state || ""}
                              onChange={(e) =>
                                handleInputChange(
                                  "address.state",
                                  e.target.value,
                                )
                              }
                              placeholder="MI"
                              maxLength={10}
                              className="backdrop-blur-xl bg-white/5 border border-white/10"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Palette className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Stile Biglietto
                          </Label>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-end gap-2">
                            <div className="flex-1 space-y-2">
                              <Label
                                htmlFor="gradientColor1"
                                className="text-sm"
                              >
                                Colore 1
                              </Label>
                              <ColorPiker
                                direction="left"
                                value={card.primary_color || "#EA580C"}
                                onChange={(val: any) =>
                                  handleInputChange("primary_color", val)
                                }
                              />
                            </div>

                            <div
                              className="flex-shrink-0 tooltip"
                              data-tooltip="Genera colore secondario automatico"
                            >
                              <Button
                                type="button"
                                variant="outline"
                                size="lg"
                                onClick={handleAutoColor}
                                className="backdrop-blur-xl bg-white/5 border border-white/10 flex flex-col justify-center gap-0 w-16"
                              >
                                <span className="text-xs">Auto</span>
                                <ArrowRight className="size-4" />
                              </Button>
                            </div>

                            <div className="flex-1 space-y-2">
                              <Label
                                htmlFor="gradientColor2"
                                className="text-sm"
                              >
                                Colore 2
                              </Label>
                              <ColorPiker
                                direction="right"
                                value={card.bg_value || "#F97316"}
                                onChange={(val: any) =>
                                  handleInputChange("bg_value", val)
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Sfondo</Label>
                          <Select
                            value={card.theme_mode || "dark"}
                            onValueChange={(value) =>
                              handleInputChange("theme_mode", value)
                            }
                          >
                            <SelectTrigger className="backdrop-blur-xl bg-white/5 border border-white/10 w-full">
                              <SelectValue placeholder="Seleziona tema sfondo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="dark">
                                <div className="flex items-center">
                                  <Moon className="h-4 w-4 mr-2" />
                                  <span>Scuro</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="light">
                                <div className="flex items-center">
                                  <Sun className="h-4 w-4 mr-2" />
                                  <span>Chiaro</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <ListOrdered className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">
                            Ordine sezioni
                          </Label>
                        </div>

                        <IonList
                          style={{ background: "transparent", marginTop: 0 }}
                        >
                          <IonReorderGroup
                            disabled={false}
                            onIonItemReorder={(e: CustomEvent) => {
                              const from = (e as any).detail.from;
                              const to = (e as any).detail.to;
                              const copy = [...(card.section_order ?? [])];
                              const [moved] = copy.splice(from, 1);
                              copy.splice(to, 0, moved);
                              handleSectionOrderChange(copy);
                              (e as any).detail.complete();
                            }}
                          >
                            {card.section_order?.map((s) => (
                              <IonItem
                                key={s}
                                style={{ marginBottom: 0 }}
                                className="item-style"
                                lines="full"
                              >
                                <Label className="text-sm">
                                  {s === "company"
                                    ? "Company Data"
                                    : s === "address"
                                    ? "Indirizzo"
                                    : s === "secondary"
                                    ? "Contatti secondari"
                                    : "Social"}
                                </Label>
                                <IonReorder slot="end" />
                              </IonItem>
                            ))}
                          </IonReorderGroup>
                        </IonList>
                      </div>
                    </div>
                  </GlassCard>
                  <div className="h-8 sm:h-16"></div>
                </div>
              </div>

              <div
                className={cn(
                  "h-full overflow-y-hidden transition-all duration-300 ease-in-out",
                  isMobileView
                    ? [
                        "absolute inset-0",
                        showPreview
                          ? "translate-x-0 opacity-100"
                          : "translate-x-full opacity-0",
                      ]
                    : "relative", // desktop: sempre visibile e affiancato
                )}
              >
                <div className="px-0 lg:px-1 h-full">
                  <BusinessCardPreview card={card} showActions={false} />
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
            <div className="flex justify-center w-full">
              <div className="flex gap-2 lg:gap-4 sm:me-4 items-center p-2 bg-[#181818E0] rounded-xl mb-2 sm:mb-6">
                <Button
                  size="lg"
                  className="hover:opacity-75 bg-[#323539]"
                  onClick={submit}
                >
                  <IonIcon className="h-5 w-5" icon={saveOutline} />
                  {card.id ? "Aggiorna" : "Salva"}{" "}
                  {isLoadingSave && <Spinner />}
                </Button>
                {!showPreview ? (
                  <>
                    {isMobileView && (
                      <Button
                        className="bg-[#323539] hover:opacity-75 flex items-center gap-2"
                        onClick={() => setShowPreview(true)}
                        size="lg"
                      >
                        <IonIcon className="h-5 w-5" icon={eyeOutline} />
                        <span>Anteprima</span>
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      size="lg"
                      className="bg-[#323539] hover:opacity-75 flex items-center gap-2"
                      onClick={() => setShowPreview(false)}
                    >
                      <IonIcon className="h-5 w-5" icon={createOutline} />
                      <span>Continua</span>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </IonContent>

      <IonFooter
        style={{
          "--background": "transparent",
          borderTop: "1px solid rgba(255,255,255,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "10px 0",
          }}
        >
          <a
            href="https://www.effai.it"
            target="_blank"
            rel="noopener noreferrer"
            className="gradient-text"
          >
            <img
              className="h-4 xs:h-6 sm:h-8"
              src="/assets/long_light.png"
              alt="logo"
              height={24}
              style={{ borderRadius: 4 }}
            />
          </a>

          <span
            style={{
              color: "rgba(255,255,255,0.8)",
            }}
            className="text-[0.6rem] xs:text-xs md:text-base"
          >
            Intelligenza Artificiale su misura per te!{" "}
            <a
              href="https://www.effai.it"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#00dbd4",
                textDecoration: "underline",
              }}
            >
              Scopri come
            </a>
          </span>
        </div>
      </IonFooter>
    </IonPage>
  );
}
